import {
  styleAttr,
  styleAttrUi,
  animationStates,
  animationUi,
  ComponentAdmin,
  PublishesTo,
  SubscribesTo,
  OptionalString,
} from '@backstage-components/base';
import {Static, Type} from '@sinclair/typebox';

export const reactName = 'RtcRoomContainer';
export const name = reactName;
export const description = reactName;

export const schema = Type.Object({
  roomName: OptionalString({title: 'Display Name'}),
  testToken: OptionalString({title: 'Test token'}),
  requestVideoDevices: Type.Optional(
    Type.Boolean({
      title: 'Video devices?',
      default: true,
      description: 'Request access to video devices.',
    })
  ),
  requestAudioDevices: Type.Optional(
    Type.Boolean({
      title: 'Audio devices?',
      default: true,
      description: 'Request access to audio devices.',
    })
  ),
  animationStates,
  styleAttr,
});

export type SchemaType = Static<typeof schema>;

export const uiSchema = {
  ...styleAttrUi,
  ...animationUi,
};

export const defaultFieldData: SchemaType = {
  styleAttr: '',
  animationStates: [],
  roomName: '',
  testToken: '',
  requestAudioDevices: true,
  requestVideoDevices: true,
};

export const Speaker = Type.Object({
  sid: Type.String(),
  audioLevel: Type.Number(),
  identity: Type.String(),
  isSpeaking: Type.Boolean(),
  name: Type.Optional(Type.String()),
  metadata: Type.Optional(Type.String()),
  isLocal: Type.Boolean(),
});

export type Speaker = Static<typeof Speaker>;

export const instructions = Type.Union([
  PublishesTo({
    topic: `${reactName}:on-active-speaker-changed`,
    meta: {
      participants: Type.Array(Speaker),
    },
  }),
  PublishesTo({
    topic: `${reactName}:on-speaker-start`,
    meta: {
      ...Speaker.properties,
    },
  }),
  PublishesTo({
    topic: `${reactName}:on-speaker-stop`,
    meta: {
      ...Speaker.properties,
    },
  }),
  PublishesTo({
    topic: `${reactName}:on-audio-playback-change`,
    description: 'Audio playback status has changed.',
  }),
  PublishesTo({
    topic: `${reactName}:on-connection-quality-changed`,
    meta: {
      quality: Type.String(),
      participantSid: Type.String(),
    },
  }),
  PublishesTo({
    topic: `${reactName}:on-local-track-published`,
  }),
  PublishesTo({
    topic: `${reactName}:on-local-track-unpublished`,
  }),
  PublishesTo({
    topic: `${reactName}:on-media-devices-error`,
  }),
  PublishesTo({
    topic: `${reactName}:on-media-devices-changed`,
  }),
  PublishesTo({
    topic: `${reactName}:on-state-changed`,
  }),
  PublishesTo({
    topic: `${reactName}:on-track-muted`,
  }),
  PublishesTo({
    topic: `${reactName}:on-track-unmuted`,
  }),
  PublishesTo({
    topic: `${reactName}:on-track-published`,
  }),
  PublishesTo({
    topic: `${reactName}:on-track-unpublished`,
  }),
  PublishesTo({
    topic: `${reactName}:on-track-subscribed`,
  }),
  PublishesTo({
    topic: `${reactName}:on-track-unsubscribed`,
  }),
  SubscribesTo({
    topic: `${reactName}:request-device-permission`,
  }),
  PublishesTo({
    topic: `${reactName}:on-device-permission-granted`,
    meta: {
      videoDevices: Type.Array(
        Type.Object({
          label: Type.String(),
          kind: Type.String(),
          deviceId: Type.String(),
        })
      ),
      audioDevices: Type.Array(
        Type.Object({
          label: Type.String(),
          kind: Type.String(),
          deviceId: Type.String(),
        })
      ),
      activeVideoDeviceId: Type.Optional(Type.String()),
      activeAudioDeviceId: Type.Optional(Type.String()),
    },
  }),
  PublishesTo({
    topic: `${reactName}:on-device-permission-denied`,
    meta: {
      deviceError: Type.String(),
    },
  }),
]);

export const ComponentDefinition: ComponentAdmin = {
  id: '5c05a93a-846e-4521-81c7-5e6062068779',
  reactName,
  name,
  slug: reactName,
  description,
  version: 1,
  defaultFieldData,
  slotConfiguration: {
    items: {maxModules: 100, displayWidth: 12, title: 'Modules'},
  },
  schema,
  uiSchema,
  instructions,
};
